import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { ModalDialog, FieldList } from 'axway-react-ui';

class CompositeFieldsSelect extends ModalDialog.Content {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.selectField = this.selectField.bind(this);
		this.changeParent = this.changeParent.bind(this);
		this.getSelectedRows = this.getSelectedRows.bind(this);
		this.getJoinModels = this.getJoinModels.bind(this);
		const initialParent = props.modelDef.parent;
		const initialFields
			= (props.models[initialParent] && props.models[initialParent].fields) || [];
		this.state = {
			__fields__: {},
			parent: initialParent,
			selectedRows: this.getSelectedRows(initialParent, props),
			fields: Object.keys(initialFields)
		};
	}

	/**
	 * [selectField determine which fields were selected based on indexes]
	 * @param  {[array]} indexes
	 */
	selectField(indexes) {
		const selectedFields = [];
		indexes.forEach(index => {
			selectedFields.push(this.state.fields[index]);
		});
		this.props.onChange({ fields: selectedFields, parent: this.state.parent });
	}

	/**
	 * [changeParent change the parent model to change fields list]
	 * @param  {[string]} model to look at it fields
	 */
	changeParent(parentName) {
		const fields
			= (this.props.models[parentName] && this.props.models[parentName].fields) || [];
		const change = {
			selectedRows: this.getSelectedRows(parentName), // [1, 3, 4]
			parent: parentName,
			fields: Object.keys(fields)
		};
		this.setState(change);
	}

	/**
	 * Determine which indexes correspond to fields
	 * @param  {[string]} parent [name of the model to look for fields in]
	 */
	getSelectedRows(parent, props = this.props) {
		const { models, modelDef } = this.props;
		const { selectedRows } = props;
		if (Object.keys(selectedRows).length === 0) {
			return [];
		}
		if (selectedRows.hasOwnProperty(parent)) {
			const fieldNames = selectedRows[parent];
			const indexes = [];

			if (fieldNames.length === 0) {
				return [];
			}
			const fields = Object.keys(models[parent].fields || {});
			fieldNames.forEach(name => {
				const fieldName = (modelDef.fields[name] && modelDef.fields[name].name) || name;
				const index = fields.indexOf(fieldName);
				if (index > -1) {
					indexes.push(index);
				}
			});
			return indexes;
		}

		return [];
	}

	/**
	 * [getJoinModels determine which models to include in dropdown]
	 * @param  {[object]} model [current definition of the model (modelDef)]
	 * @return {[array]} [array of modelNames that are related to the modelDef]
	 */
	getJoinModels(model) {
		const exemptModels = [];
		const avoidedTypes = [ 'object', 'array' ];
		const dropDownModels = [];
		const { joinedModels, joins } = model._joinmeta_;
		const parent = model.parent;

		if (!joins || joins.length === 0) {
			return [ parent ];
		}

		joins.forEach(join => {
			if (join.merge && avoidedTypes.indexOf(join.merge) >= 0 && join.model) {
				exemptModels.push(join.model);
			}
		});

		dropDownModels.push(parent);
		if (joinedModels) {
			joinedModels.forEach(modelName => {
				if (exemptModels.indexOf(modelName) < 0) {
					dropDownModels.push(modelName);
				}
			});
		}
		return dropDownModels;
	}

	componentWillReceiveProps(nextProps) {
		// Update the indexes to pre-select
		this.setState({
			selectedRows: this.getSelectedRows(this.state.parent, nextProps)
		});
	}

	render() {
		// There appears to be a bug in TableRow (used by fieldlist) where it wants an
		// onChange for CheckBox.
		const { models, modelDef } = this.props;

		return (
			<div className="composite-fields-select">
				<div className="ux-container">
					<DropdownButton
						onSelect={this.changeParent}
						bsSize="large"
						id="default-dropdown"
						title={this.state.parent}
					>
						{
							this.getJoinModels(modelDef).map((modelName, i) => {
								return (
									<MenuItem
										eventKey={modelName}
										key={i}
									>
										{modelName}
									</MenuItem>
								);
							})
						}
					</DropdownButton>
				</div>
				<div className="ux-container fields-container">
					<FieldList
						selectable
						onSelection={this.selectField}
						selectedRows={this.state.selectedRows}
					>
						{
							this.state.fields.map((fieldName, i) => {
								return (
									<FieldList.Field
										key={i}
										name={fieldName}
										type={
											models[this.state.parent].fields[fieldName].type
										}
										description={
											models[this.state.parent].fields[fieldName].description
										}
									/>
								);
							})
						}
					</FieldList>
				</div>
			</div>
		);
	}
}

CompositeFieldsSelect.propTypes = {
	modelDef: (props, propName, componentName) => {
		if (!props[propName]) {
			return new Error(`${propName} is required`);
		}
		if (!props[propName].parent) {
			return new Error(`'parent' is required in modelDef for use in ${componentName}`);
		}
	},
	models: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	selectedRows: PropTypes.object.isRequired
};

export default injectIntl(CompositeFieldsSelect);
